import fitTextAnnotationToContent from './imageEditor/src/utils/fitTextAnnotationToContent';
import { createGroupId } from './imageEditor/src/utils/annotationGroupUtils';
import {
  getTextTemplateCanvasStyle,
  templateUsesPreviewCard,
  type TextTemplateItem,
} from './textTemplateCatalog';
import type { UiTheme } from './theme/uiTheme';

/** Matches picker effect tile / `--theme-action-surface` (dark). */
export const TEXT_TEMPLATE_CARD_FILL = '#181c21';
export const TEXT_TEMPLATE_CARD_PADDING_X = 14;
export const TEXT_TEMPLATE_CARD_PADDING_Y = 10;
export const TEXT_TEMPLATE_CARD_CORNER_RADIUS = 10;

export type TextTemplateInsertionResult = {
  annotations: Record<string, Record<string, unknown>>;
  selectionsIds: string[];
  textId: string;
};

/**
 * Builds text (+ optional grouped background rect) for UPDATE_STATE.
 * Rect is stacked under text; both share `groupId` when the template uses a preview card.
 */
export const buildTextTemplateInsertion = (
  textDefaults: Record<string, unknown>,
  template: TextTemplateItem,
  theme: UiTheme,
): TextTemplateInsertionResult => {
  const idSeed = Date.now() * Math.random();
  const textId = `Text-${parseInt(String(idSeed), 10)}`;
  const style = getTextTemplateCanvasStyle(theme, template);
  let textAnnotation =
    fitTextAnnotationToContent({ ...textDefaults, id: textId, ...style }) ||
    ({ ...textDefaults, id: textId, ...style } as Record<string, unknown>);

  if (!templateUsesPreviewCard(template)) {
    return {
      annotations: { [textId]: textAnnotation },
      selectionsIds: [textId],
      textId,
    };
  }

  const rectId = `Rect-${parseInt(String(idSeed + 1), 10)}`;
  const groupId = createGroupId();
  const padX = TEXT_TEMPLATE_CARD_PADDING_X;
  const padY = TEXT_TEMPLATE_CARD_PADDING_Y;
  const tx = Number(textAnnotation.x) || 0;
  const ty = Number(textAnnotation.y) || 0;
  const tw = Number(textAnnotation.width) || 0;
  const th = Number(textAnnotation.height) || 0;

  const rectAnnotation: Record<string, unknown> = {
    id: rectId,
    name: 'Rect',
    groupId,
    templateCardBackground: true,
    fill: TEXT_TEMPLATE_CARD_FILL,
    stroke: '#000000',
    strokeWidth: 0,
    opacity: 1,
    x: tx - padX,
    y: ty - padY,
    width: tw + padX * 2,
    height: th + padY * 2,
    cornerRadius: TEXT_TEMPLATE_CARD_CORNER_RADIUS,
    rotation: textAnnotation.rotation ?? 0,
    scaleX: 1,
    scaleY: 1,
    shadowOffsetX: 0,
    shadowOffsetY: 0,
    shadowBlur: 0,
    shadowColor: '#000000',
    shadowOpacity: 1,
  };

  textAnnotation = { ...textAnnotation, groupId };

  // Key order: card first, text last → Konva / stack sync draws text above the card.
  return {
    annotations: {
      [rectId]: rectAnnotation,
      [textId]: textAnnotation,
    },
    selectionsIds: [textId],
    textId,
  };
};

export { templateUsesPreviewCard };
